/**
 * Main App Component
 * Handles authentication routing for the workspace + conversation architecture.
 */

import * as React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { SessionProvider } from "@/features/auth/components/SessionProvider";
import { AnalyticsIdentify } from "@/features/analytics/AnalyticsIdentify";
import { track } from "@/lib/analytics";
import { useSession } from "@/features/auth/hooks/useSession";
import { DiagHost } from "@/app/DiagHost";
import { useGateStateDiag } from "@/common/hooks/useGateStateDiag";
import { AppProviders } from "./providers/AppProviders";
import { FeedbackProvider } from "./providers/FeedbackContext";
import { FeedbackModal } from "@/layout/FeedbackModal";
import { MagicLinkVerify } from "../views/MagicLinkVerify";
import { LoginV2 } from "../views/LoginV2";
import { PlanGate } from "../features/billing/components/PlanGate";
import { VersionedTermsGate } from "../features/auth/components/VersionedTermsGate";
import { ConnectPage } from "../features/auth/components/ConnectPage";
import { SsoAutoConnect } from "../features/auth/components/SsoAutoConnect";
import { resolveZeroWorkspaceDestination } from "@/features/auth/lib/connect-first-routing";
import { ssoAutoConnectAttempted } from "@/features/auth/lib/sso-autoconnect";
import {
  ConversationProvider,
  useConversation,
} from "./providers/ConversationContext";
import { useConversation as useConversationDetailQuery } from "@/features/conversations/hooks";
import MainLayout from "../layout/MainLayout";
import { WorkspaceSettings } from "../views/settings/WorkspaceSettings";
import { queryClient } from "../lib/query/client";
import { apiFetch } from "../lib/apiClient";
import { PAYWALL_DISMISSED_KEY } from "../lib/authStorage";
import { ErrorBoundary } from "../common/components/ErrorBoundary";
import { SessionErrorBanner } from "../layout/SessionErrorBanner";
import { ConnectionErrorScreen } from "../common/components/ConnectionErrorScreen";
import { SuspendedAccountScreen } from "../common/components/SuspendedAccountScreen";
import { BillingIssueModalHost } from "../features/billing/components/BillingIssueModalHost";
import { PlanChoiceModal } from "@/features/billing/components/PlanChoiceModal";
import { FeedbackConversationBridge } from "./providers/FeedbackConversationBridge";
import { MaintenanceOverlay } from "../common/components/MaintenanceOverlay";
import { isMaintenanceModeActive } from "../lib/maintenance";
import { resolveWorkspaceOnboarding } from "@/features/auth/lib/workspace-onboarding";
import { shouldUseRecipeOnboarding } from "@/features/onboarding/recipes/recipeEntry";
// OnboardingRecipeHost is retained for the /dev/onboarding preview route only —
// the post-login gate no longer renders it (chat-window onboarding replaces it).
import { OnboardingRecipeHost } from "@/features/onboarding/OnboardingRecipeHost";
import { OnboardingActiveProvider } from "@/app/providers/OnboardingActiveContext";
import { XaviorPulseLoader } from "../common/components/XaviorPulseLoader";
import { WoodCanvas } from "../common/components/WoodCanvas";
import { WorkspaceNameSetupScreen } from "../common/components/WorkspaceNameSetupScreen";
import { suggestWorkspaceName } from "../common/utils/workspace-name";
import {
  shouldShowWorkspaceNameSetup,
  hasWorkspaceNameSkipFlag,
  setWorkspaceNameSkipFlag,
} from "./workspace-name-setup-gate";
import { ConversationRouteSync } from "./ConversationRouteSyncComponent";
import { WorkspaceSetupScreen } from "../common/components/WorkspaceSetupScreen";
import { listWorkspaceModules } from "../lib/api/endpoints/workspaces";
import { PortalApp } from "@/portal/PortalApp";
import { useGlobalAutoHideScrollbars } from "@/common/hooks";
import { NutritionConnect } from "@/views/NutritionConnect";
import { NativeInitializer } from "../layout/NativeInitializer";
import { DeepLinkHandler } from "../layout/DeepLinkHandler";
import { OpenInAppBanner } from "../common/components/OpenInAppBanner";
import { ToastProvider, useToast } from "./providers/ToastContext";
import { useFeedback } from "./providers/FeedbackContext";
import {
  connectProvider,
  describeConnectReturnError,
} from "@/features/oauth/lib/connect-provider";
import {
  buildConversationPath,
  resolveWorkspaceId,
} from "@/lib/workspace-routing";
import { queryKeys } from "@/lib/query/keys";
import { useWorkspaceModuleQueries } from "@/layout/sidebar/hooks/useWorkspaceModuleQueries";
import { useModulePane } from "./providers/ModulePaneContext";
import { useIsCompactLayout } from "@/common/hooks/useIsCompactLayout";
import { resolveInitialModule } from "./initial-module";
import { fetchInitialPaneState } from "@/lib/conversation-pane-state";
import { prefetchModuleChunk } from "@/lib/prefetch-module";
import { useWorkspacePlanTier } from "@/features/billing/hooks/useWorkspacePlanTier";
import { initWebKeyboardDetection } from "@/lib/capacitor";
import { captureOnboardingObParam } from "@/features/onboarding/onboarding-recipes";

// Initialize mobile web keyboard detection early, before any components mount
initWebKeyboardDetection();

// Handle OAuth redirects from providers that only allow origin URLs (e.g., Whoop).
// These providers redirect to https://app.xavior.ai?code=XXX&state=YYY instead of
// the backend callback URL. Detect this and forward to the API for token exchange.
(function handleOAuthRedirect() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  if (
    code &&
    state &&
    (window.location.pathname === "/" ||
      window.location.pathname === "/integrations/callback")
  ) {
    // Resolve API base URL using same logic as env.ts
    const explicit = (import.meta.env.VITE_API_BASE_URL as string) || "";
    const apiBase =
      explicit ||
      (import.meta.env.PROD ? "https://api.xavior.ai" : "") ||
      (window.location.hostname === "app.xavior.ai"
        ? "https://api.xavior.ai"
        : "");
    if (!apiBase) return; // No API URL resolved — don't redirect to relative path
    const callbackUrl = `${apiBase}/integrations/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
    window.location.replace(callbackUrl);
  }
})();

/** How long to wait for the container to become ready before showing the error screen. */
const CONTAINER_WARMUP_TIMEOUT_MS = 20_000;

/**
 * Holds the loader until the rendered state is fully ready — no intermediate
 * frames where the shell has painted but data hasn't arrived yet. Six gates, in
 * order: (1) initial conversations-list fetch; (2) URL not yet resolved (still
 * at `/`, waiting for ConversationRouteSync); (3) conversation detail fetch not
 * complete — the LIST returns `modules: []`, real modules come only from the
 * detail fetch, so without this MainLayout paints once with no pane then again
 * when modules arrive; (4) `activeConversation` not yet synced to the URL id;
 * (5) workspace module list + sidebar prefs (rail filter/order source) not yet
 * settled, else the rail first-paints DEFAULT_VISIBILITY/DEFAULT_RAIL_ORDER then
 * re-filters; (6) initial module pane not yet resolved + seeded into the pane
 * authority, else the pane flashes Chat before the last-open module renders.
 * Gates 4-6 are bypassed during the OAuth-callback window (oauthBypassActive):
 * the user already paid the round-trip, so we don't stack another shell
 * transition. Subscribing to the detail query here also kicks it off if nothing
 * else has — no dependency on `setActiveConversation` firing first.
 */
function AuthenticatedAppShell({ onboardingActive = false }: { onboardingActive?: boolean }) {
  const { isLoading, allConversations, error, activeConversation, patchActiveModules } =
    useConversation();
  const { conversationId, moduleId, view } = useParams<{
    conversationId?: string;
    moduleId?: string;
    view?: string;
  }>();
  const loc = useLocation();
  const navigate = useNavigate();
  const isInitialLoading = isLoading && allConversations.length === 0 && !error;

  const detailQuery = useConversationDetailQuery(conversationId ?? null);

  // OAuth-callback bypass: the backend redirects to ?integration_connected=true
  // after a provider scope grant. The user already paid the OAuth round-trip, so
  // we don't want Gate 4 to add another shell transition. Capture the URL flag at
  // first render and hold the bypass for ~10s — long enough for activeConversation
  // to sync without a session-long bypass that re-introduces the Gate 4 flicker.
  const [oauthBypassActive, setOauthBypassActive] = React.useState(() => {
    try {
      return (
        new URLSearchParams(window.location.search).get(
          "integration_connected",
        ) === "true"
      );
    } catch {
      return false;
    }
  });
  React.useEffect(() => {
    if (!oauthBypassActive) return;
    const t = setTimeout(() => setOauthBypassActive(false), 10000);
    return () => clearTimeout(t);
  }, [oauthBypassActive]);

  // ── Gate 5 inputs: the workspace-module queries the sidebar/rail filter and
  // order from. Subscribing here (same query keys the rail uses) warms the React
  // Query cache before MainLayout mounts, so the rail's first paint shows the
  // user's final enabled set + order instead of DEFAULT_VISIBILITY/_RAIL_ORDER. ──
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const { modulesReady } = useWorkspaceModuleQueries(token, workspaceId);

  // ── Gate 6 inputs: resolve the initial module pane BEFORE first paint so the
  // shell shows the correct open module (or chat) immediately — no Chat→module
  // pop-in on login / hard refresh. Confined to the first authenticated render
  // (desktop, non-OAuth) so mid-session conversation switches are untouched. ──
  const isCompact = useIsCompactLayout();
  const { setPaneModuleId, setPaneVisible, getPaneState } = useModulePane();
  const [initialModuleResolved, setInitialModuleResolved] =
    React.useState(false);
  const initialResolveStartedRef = React.useRef(false);

  // The authoritative server "last-open pane" for this conversation (the
  // conversation panel's persisted chatPane state) — the same source ChatBox
  // restores from, read here so the pane authority is correct on first paint.
  const initialPaneQuery = useQuery({
    queryKey: queryKeys.conversations.initialPane(conversationId ?? ""),
    queryFn: () =>
      fetchInitialPaneState(token ?? null, workspaceId, conversationId as string),
    enabled: Boolean(
      token && workspaceId && conversationId && !isCompact && !oauthBypassActive,
    ),
    staleTime: 30_000,
  });

  // Bare effect (runs each render, no-op after the one-shot ref flips): it must
  // fire as soon as its prerequisites become ready, whichever render that is.
  React.useEffect(() => {
    // First authenticated render only: never re-gate or re-seed on mid-session
    // conversation switches (ChatBox's hydration owns those — ADR 028).
    if (initialResolveStartedRef.current) return;
    if (loc.pathname === "/settings") return;
    if (allConversations.length === 0 || !conversationId) return;
    // Prerequisites: detail (real modules) + activeConversation synced + the
    // rail's module data settled — i.e. exactly the data the first paint needs.
    if (!detailQuery.data || !activeConversation || activeConversation.id !== conversationId) return;
    if (!modulesReady) return;

    // Compact/mobile, the OAuth fast-path, and chat-window onboarding keep the
    // existing behavior — no pre-seed; release the gate immediately. (During
    // onboarding the pane is the chat view — no module open — so there is no
    // module pane to pre-resolve.)
    if (isCompact || oauthBypassActive || onboardingActive) {
      initialResolveStartedRef.current = true;
      setInitialModuleResolved(true);
      return;
    }

    // The server last-open pane query must have settled (data — possibly null —
    // or errored). On error we treat it as "no persisted pane" and fall through.
    const paneSettled =
      initialPaneQuery.data !== undefined || initialPaneQuery.isError;
    if (!paneSettled) return;

    initialResolveStartedRef.current = true;

    const modules = detailQuery.data.conversation?.modules ?? [];

    // The pane RENDERS from activeConversation.modules (ChatBox's
    // baseConversationModules), not the pane authority. On a refresh/login of
    // the already-active conversation, ConversationContext restores it from the
    // conversations LIST (which carries modules: []) and the route-sync
    // optimization skips the detail-fetching setActiveConversation — so those
    // modules would otherwise only arrive via ChatBox's async panel fetch (the
    // module-content pop-in). Seed them from the detail snapshot we already hold
    // so the seeded pane has its module to render on the first paint. The list
    // query's staleTime keeps this from being clobbered; ChatBox's panel-bound
    // modules take over as the steady-state source.
    if (modules.length > 0 && (activeConversation.modules?.length ?? 0) === 0) {
      patchActiveModules(conversationId, () => modules);
    }

    let rememberedModuleId: string | null = null;
    try {
      rememberedModuleId =
        localStorage.getItem(`lastModule:${conversationId}`) ||
        sessionStorage.getItem(`lastModule:${conversationId}`);
    } catch {
      rememberedModuleId = null;
    }
    const persistedPane =
      initialPaneQuery.data && !initialPaneQuery.isError ? initialPaneQuery.data : null;

    const resolution = resolveInitialModule({
      moduleIdParam: moduleId ?? null,
      view: view ?? null,
      modules,
      persistedPane,
      rememberedModuleId,
      isCompact,
    });

    // Seed the pane authority with BACKGROUND provenance (intent:false). There
    // is no user gesture during the loader hold, so this can never outrank a
    // click; ChatBox's mount hydration re-asserts the same value idempotently.
    const pane = getPaneState();
    if (resolution.moduleId !== pane.moduleId) {
      setPaneModuleId(resolution.moduleId, { intent: false });
    }
    if (resolution.open !== pane.visible) {
      setPaneVisible(resolution.open, { intent: false });
    }

    // Canonicalize the URL so MainLayout's URL guard + ChatBox's hydration both
    // settle on the SAME module (URL-wins) rather than re-navigating post-paint.
    let targetPath: string | null = null;
    if (resolution.open && resolution.moduleId) {
      if (moduleId !== resolution.moduleId) {
        targetPath = buildConversationPath(workspaceId, conversationId, {
          moduleId: resolution.moduleId,
          search: loc.search,
        });
      }
      // Warm the chosen module's lazy chunk so the first paint shows the module
      // itself, not a code-split fallback.
      const mod = modules.find((m) => m.id === resolution.moduleId);
      if (mod) prefetchModuleChunk(mod.type);
    } else if (view !== "chat") {
      // Explicit chat-only — prevents MainLayout's default-module auto-open.
      targetPath = buildConversationPath(workspaceId, conversationId, {
        view: "chat",
        search: loc.search,
      });
    }
    if (targetPath) navigate(targetPath, { replace: true });

    setInitialModuleResolved(true);
  });

  // Render the workspace settings route. This early return MUST come after all
  // hooks above — placing it before them changes the hook count between renders
  // (the shell re-renders in place across route changes, it doesn't remount),
  // which trips React's rules-of-hooks and crashes the shell, so /settings never
  // appears.
  if (loc.pathname === "/settings") {
    return <WorkspaceSettings />;
  }

  if (isInitialLoading) {
    return <XaviorPulseLoader />;
  }

  // Initial fetch failed with no cached data — show a retry screen rather than
  // dropping the user into MainLayout's empty-state spinner.
  if (error && allConversations.length === 0) {
    return (
      <ConnectionErrorScreen
        error={error}
        onRetry={() => window.location.reload()}
      />
    );
  }

  const hasConversations = allConversations.length > 0;

  // Gate 2: URL still at `/` — ConversationRouteSync's effect is queued.
  if (hasConversations && !conversationId) {
    return <XaviorPulseLoader />;
  }

  // Gate 3: URL has conversationId but the detail response (with real modules)
  // hasn't landed yet. Holding here guarantees MainLayout's first paint has
  // full module data — no "chat full-width → module appears" flash.
  //
  // Bypassed during chat-window onboarding (like Gates 4 and 6, but NOT gate 5):
  // the pane is the chat view — no module open — so there is no module pane to
  // wait on, and — critically — this gate is a
  // ChatBox REMOUNT vector. The welcome conversation's detail query can
  // transiently drop `data` (window-focus refetch, a `refreshSession`-driven
  // invalidation, or a key flip during route canonicalization); each time it
  // does, this gate swaps MainLayout → loader → MainLayout, UNMOUNTING and
  // REMOUNTING ChatBox mid-onboarding. The orchestration now survives remounts
  // (module-level session registry in useChatOnboarding), but bypassing here
  // also lets the chat subtree mount once and STAY mounted through onboarding,
  // mirroring the existing oauthBypass pattern.
  if (
    !onboardingActive &&
    hasConversations &&
    conversationId &&
    (detailQuery.isLoading || !detailQuery.data)
  ) {
    return <XaviorPulseLoader />;
  }

  // Gate 4: detail has loaded but the ConversationContext hasn't synced
  // `activeConversation` to the URL's conversationId yet. Without this,
  // MainLayout briefly renders its no-conversation fallback (with the
  // inline pulse loader) before the route-sync effect catches up — the
  // user perceives this as the loader handing off to a smaller loader,
  // then back to the loader, then to chat.
  // Bypassed during the OAuth-callback window (see oauthBypassActive above).
  if (
    !oauthBypassActive &&
    !onboardingActive &&
    hasConversations &&
    conversationId &&
    (!activeConversation || activeConversation.id !== conversationId)
  ) {
    return <XaviorPulseLoader />;
  }

  // Gate 5: the sidebar rail filters + orders modules from the workspace module
  // list and sidebar preferences. They are owned by SidebarRail and would
  // otherwise fetch only after it mounts — so its first paint would fall back to
  // DEFAULT_VISIBILITY / DEFAULT_RAIL_ORDER and then visibly re-filter/re-order.
  // We subscribe above (same query keys) and hold until both settle, so the
  // rail's first paint is already the user's final enabled set + order.
  // `modulesReady` is true when there is no token/workspace, and releases on
  // query error (renders defaults) — so it can never deadlock the shell.
  // Applies during chat-window onboarding too (unlike Gates 3/4/6): the rail is
  // now rendered from first paint, so onboarding must hold here until the module
  // data is ready — that is what makes the rail and chat load TOGETHER at
  // shell-readiness (matching the returning-user reveal) instead of the rail
  // popping in later. Bypassed only during the OAuth-callback window like Gates
  // 4 and 6, so a cold cache on the OAuth redirect doesn't stack another loader
  // transition on top of the round-trip the user already paid.
  if (!oauthBypassActive && !modulesReady) {
    return <XaviorPulseLoader />;
  }

  // Gate 6: the initial module pane hasn't been resolved + seeded yet (the
  // effect above). Holding here guarantees MainLayout/ChatBox's first paint
  // reads the correct open module (or chat) from the pane authority — no
  // Chat→module pop-in on login / hard refresh. Self-releases on compact or
  // query error (see the effect), so it never deadlocks. Bypassed during the
  // OAuth-callback window for the same reason as Gate 4 (the user already paid
  // the OAuth round-trip — don't add another shell transition on top).
  if (
    !oauthBypassActive &&
    !onboardingActive &&
    hasConversations &&
    conversationId &&
    !initialModuleResolved
  ) {
    return <XaviorPulseLoader />;
  }

  return <MainLayout onboardingActive={onboardingActive} />;
}

function AppContent() {
  const {
    token,
    profile,
    status,
    error,
    refresh,
    setToken,
    clearSession,
    containerReady,
    pendingInvites,
    selectedContainer,
    containers,
    containersLoaded,
    sessionError,
  } = useSession();
  const workspacePlanTier = useWorkspacePlanTier();
  // App-wide canonical auto-hide scrollbars: one document-level capture listener
  // toggles `.is-scrolling` on whatever scrolled, so every `.xv-scroll` surface
  // (modules, modals/dropdowns via portals, login, portal sub-app) reveals the
  // thumb while scrolling and hides it on idle. Mounted at the app root so the
  // controller is always present wherever `.xv-scroll` is used.
  useGlobalAutoHideScrollbars();
  // One-time gate: a freshly-created team workspace is named "Personal", so
  // prompt the owner to name it (+ themselves) before the chat loads. Set true
  // once they save/skip so it doesn't re-show within the session.
  const [workspaceNameDismissed, setWorkspaceNameDismissed] =
    React.useState(false);
  // PDR 011 / PDR 010 D3 — SSO auto-connect fallback. The zero-workspace connect
  // leaf fires the provider grant automatically for SSO identities (skipping the
  // ConnectPage chooser). If STARTING that grant fails (authorize-URL fetch
  // error, no redirect), SsoAutoConnect flips this so the leaf falls back to the
  // manual chooser this session.
  const [ssoAutoConnectFailed, setSsoAutoConnectFailed] = React.useState(false);
  const handleSsoAutoConnectError = React.useCallback(
    () => setSsoAutoConnectFailed(true),
    [],
  );
  // Read the one-shot flag ONCE per page load. SsoAutoConnect sets it in its mount
  // effect BEFORE the async authorize-URL fetch navigates away, so reading it live
  // would let a re-render in that window swap SsoAutoConnect → ConnectPage and flash
  // the chooser. The denial guard still holds: a denial is a fresh page load that
  // re-captures the (now-set) flag → chooser.
  const ssoAutoConnectAttemptedAtMount = React.useRef(
    ssoAutoConnectAttempted(),
  ).current;
  const location = useLocation();
  const navigate = useNavigate();
  const paymentStatusRef = React.useRef<string | null>(null);

  // Capture the onboarding `?ob` recipe param at app entry — within the React
  // lifecycle (not a module-eval global write) so the persisted value can later
  // be CLEARED at a clean point (ChatBox clears it once the user acts; see
  // clearOnboardingObParam). ChatBox can mount before `?ob` lands in the final
  // URL after the connect-first/SSO/provision redirect chain, so a read at mount
  // time would memoize the default recipe; persisting here makes recipe
  // resolution (readOnboardingObParam in ChatBox) immune to mount timing. Re-runs
  // on URL change so a `?ob` that only appears after a redirect is still caught;
  // captureOnboardingObParam no-ops when the param is absent (never wipes intent).
  React.useEffect(() => {
    captureOnboardingObParam();
  }, [location.pathname, location.search]);

  // Workspace warmup stuck-state escape hatch (#2475).
  // If the container warmup doesn't resolve within 20 s, flip this flag so
  // the !containerReady branch renders ConnectionErrorScreen instead of an
  // infinite spinner.  Cleared immediately once containerReady flips to true.
  const [containerLoadingTimedOut, setContainerLoadingTimedOut] =
    React.useState(false);

  // Voluntary paywall dismissal — persisted in sessionStorage so navigations
  // within the same tab don't re-trigger the monetization/checkout gate after
  // the user clicks X. This is the MONETIZATION dismissal only; it must NOT gate
  // the team-join (pending-invite) consent gate — see teamJoinDismissed below.
  const [paywallDismissed, setPaywallDismissed] = React.useState(
    () =>
      typeof window !== "undefined" &&
      window.sessionStorage.getItem(PAYWALL_DISMISSED_KEY) === "1",
  );
  const handlePaywallDismiss = React.useCallback(() => {
    window.sessionStorage.setItem(PAYWALL_DISMISSED_KEY, "1");
    setPaywallDismissed(true);
  }, []);

  // Upgrade modal (PDR 012 Phase 2): voluntary upgrade path only (?upgrade=1 /
  // xavior:open-upgrade-modal CTA). Forced trial-expiry uses the fullscreen
  // PlanGate (expiredNeedsUpgrade). No dismissed-latch is needed — the modal
  // never auto-opens for expiry; voluntary close is handled by closeUpgrade.
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = React.useState(false);

  // Team-join (pending-invite) consent gate dismissal — kept in component state
  // only (NOT sessionStorage) so it never leaks to/from the monetization paywall
  // flag and always resets on auth change. An invitee must ALWAYS see the gate on
  // a fresh login regardless of any prior paywall dismissal; dismissing it still
  // routes per PlanGate's close logic (return-to-existing-or-logout).
  const [teamJoinDismissed, setTeamJoinDismissed] = React.useState(false);
  React.useEffect(() => {
    // Reset on USER-IDENTITY change (profile.id), NOT raw token: the proactive
    // refresh rotates the token string ~every 14 min, and keying this on `token`
    // would re-slam a dismissed gate full-screen on every refresh. profile.id is
    // stable across refreshes and only changes when a different user signs in.
    setTeamJoinDismissed(false);
  }, [profile?.id]);
  const handleTeamJoinDismiss = React.useCallback(() => {
    setTeamJoinDismissed(true);
  }, []);

  // First-time user workspace setup screen: shown while workspace bootstraps
  // for users who have not yet completed onboarding. Dismissed once modules
  // exist. Dismissal is persisted per-user in localStorage so a reload mid-
  // onboarding (onboardingComplete still false) does not re-trigger the 5s splash.
  //
  // Must be declared before the containerLoadingTimedOut effect — that
  // effect's deps array reads setupScreenDismissed, and re-ordering this
  // declaration after the effect would cause a TDZ error at runtime.
  const [setupScreenDismissed, setSetupScreenDismissed] = React.useState(false);

  React.useEffect(() => {
    if (containerReady) {
      setContainerLoadingTimedOut(false);
      return;
    }
    // Gate 6a intercepts before containerReady is ever checked, so the 20 s
    // timer result is never rendered during onboarding.  Skip it to avoid a
    // spurious state update (and the associated extra render) while the user
    // is still on WorkspaceSetupScreen.
    if (
      resolveWorkspaceOnboarding(selectedContainer, profile).needsOnboarding &&
      !setupScreenDismissed
    )
      return;
    const id = window.setTimeout(
      () => setContainerLoadingTimedOut(true),
      CONTAINER_WARMUP_TIMEOUT_MS,
    );
    return () => window.clearTimeout(id);
  }, [containerReady, profile?.onboardingComplete, selectedContainer?.onboardingCompleted, selectedContainer?.role, setupScreenDismissed]);

  // Post-onboarding entry (W6): the PlanPicker free CTA / enterApp do a full
  // reload to land in the app; on first paint the gate chain can flash a blank
  // before session/plan/onboarding/container state settles. A one-shot
  // sessionStorage marker (set just before those reloads) holds a deterministic
  // loader across that window — generalizing the oauthBypassActive idea to the
  // plan/demo entry — then clears once the app is genuinely ready (or a safety
  // timeout fires so a stuck state still falls through to the normal gates).
  const [enteringApp, setEnteringApp] = React.useState(() => {
    try {
      return sessionStorage.getItem("xavior:entering-app") === "1";
    } catch {
      return false;
    }
  });
  const appEntryReady =
    status === "authenticated" &&
    Boolean(profile) &&
    containersLoaded &&
    containerReady;
  React.useEffect(() => {
    if (!enteringApp) return;
    const clear = () => {
      try {
        sessionStorage.removeItem("xavior:entering-app");
      } catch {
        /* ignore */
      }
      setEnteringApp(false);
    };
    if (appEntryReady) {
      clear();
      return;
    }
    const t = window.setTimeout(clear, 10000);
    return () => window.clearTimeout(t);
  }, [enteringApp, appEntryReady]);

  React.useEffect(() => {
    if (!profile?.id || typeof window === "undefined") return;
    const setupSeenKey = `xavior:setup-seen:${profile.id}`;
    if (window.localStorage.getItem(setupSeenKey) === "1") {
      setSetupScreenDismissed(true);
    }
  }, [profile?.id]);

  // Zero-workspace is a VALID, authenticated state (Teams S4 / PDR 006 D4): a
  // user with no usable workspace and no pending invite (e.g. a removed member,
  // or a fresh signup whose creation failed) lands on the standalone
  // zero-workspace plan-gate below and is NEVER logged out. The old 12s
  // zero-workspace force-logout timer was removed here — the session
  // ends only on an explicit log-out elsewhere or an auth failure, never on this
  // valid state. `containersLoaded` still distinguishes a CONFIRMED-empty fetch (show the
  // gate) from an in-flight/errored one (keep the loader / retry banner), so we
  // don't flash the gate while a slow cold-start fetch is still resolving.
  const confirmedZeroWorkspace =
    status === "authenticated" &&
    Boolean(profile) &&
    containersLoaded &&
    !sessionError &&
    (containers?.length ?? 0) === 0 &&
    (pendingInvites?.length ?? 0) === 0;

  // #5105 loop diagnostics — extracted to a hook to keep this shell under the
  // file-size ceiling (see useGateStateDiag).
  useGateStateDiag({
    status,
    profile,
    containersLoaded,
    containerReady,
    appEntryReady,
    enteringApp,
    confirmedZeroWorkspace,
    containers,
    pendingInvites,
    selectedContainer,
  });

  const handleSetupReady = React.useCallback(() => {
    // Modules are provisioned — drop the splash locally and hand off to
    // MainLayout, where the agent-driven BOOTSTRAP flow runs in the
    // "Welcome" conversation and flips onboarding_completed itself via
    // PATCH /auth/profile (ChatBox.tsx onboarding.complete directive +
    // 6-turn safety net).
    if (profile?.id && typeof window !== "undefined") {
      const setupSeenKey = `xavior:setup-seen:${profile.id}`;
      try {
        window.localStorage.setItem(setupSeenKey, "1");
      } catch {
        /* quota / private mode */
      }
    }
    setSetupScreenDismissed(true);
  }, [profile?.id]);

  const handleSetupTimeout = React.useCallback(() => {
    // The WorkspaceSetupScreen handles its own timeout UI; nothing to do here.
  }, []);

  const checkWorkspaceReady = React.useCallback(async (): Promise<boolean> => {
    if (!token) return false;
    try {
      const workspaceId = selectedContainer?.id ?? selectedContainer?.ownerId;
      if (!workspaceId) return false;
      const modules = await listWorkspaceModules(token, workspaceId);
      return modules.length > 0;
    } catch {
      return false;
    }
  }, [token, selectedContainer]);

  const upgradeRequested = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("upgrade") === "1";
  }, [location.search]);

  // Forced trial-expiry flag. Lifted here (component scope) so the PlanGate
  // gate condition below can read it.
  //
  // A null trialStatus (supervisor unreachable) evaluates as gated (true) here —
  // NOT as fail-open. The outage cases split by agreements:
  //   • agreements-truthy users: caught BEFORE this by the ConnectionErrorScreen
  //     guard in getMainContent() (the `!trialStatus && agreementsAcceptedAt` check),
  //     so they never reach this memo in the outage path.
  //   • connect-first SSO users (agreementsAcceptedAt==null): the ConnectionErrorScreen
  //     guard doesn't fire for them (it requires agreementsAcceptedAt), so they fall
  //     through to this memo. Returning true lands them on the fullscreen PlanGate
  //     (gated), not the app — correct, safe behavior during an outage.
  const expiredNeedsUpgrade = React.useMemo(() => {
    if (!profile) return false;
    const isLocalhost =
      typeof window !== "undefined" && window.location.hostname === "localhost";
    const forcePaywall = import.meta.env.VITE_ENABLE_PAYWALL === "true";
    const paywallActive =
      forcePaywall ||
      (!isLocalhost &&
        (import.meta.env.PROD || import.meta.env.MODE === "production"));
    if (!paywallActive) return false;
    const isWorkspaceOwner = selectedContainer?.isOwner !== false;
    const isFreePlan = workspacePlanTier === "free";
    const tierLoading = workspacePlanTier === null;
    const trialStatus = profile.trialStatus ?? null;
    return (
      !profile.isPaidAccount &&
      isWorkspaceOwner &&
      !isFreePlan &&
      !tierLoading &&
      // null trialStatus (supervisor unreachable) → treat as expired/unknown → gate.
      // Agreements-truthy users never reach this (ConnectionErrorScreen fires first).
      (trialStatus == null ||
        trialStatus.isExpired === true ||
        (!trialStatus.isTrialActive && !trialStatus.isGracePeriod))
    );
  }, [profile, selectedContainer?.isOwner, workspacePlanTier]);

  // Auto-open the upgrade modal for voluntary (?upgrade=1) path only.
  // Forced trial-expiry now goes to the fullscreen PlanGate (see gate below).
  // Do NOT strip ?upgrade=1 here — stripping it would flip upgradeRequested to
  // false, which lets the ConnectionErrorScreen outage guard fire and dead-ends
  // a ?upgrade=1 user during an outage. The param is stripped in closeUpgrade
  // so it persists (keeping upgradeRequested true) until the user closes the modal.
  React.useEffect(() => {
    if (upgradeRequested) {
      setIsUpgradeModalOpen(true);
    }
  }, [upgradeRequested]);

  // xavior:open-upgrade-modal CustomEvent — fired from deep-tree CTAs (Sidebar,
  // SidebarRail, etc.) as an alternative to ?upgrade=1 navigation (Task 3).
  React.useEffect(() => {
    const handler = () => {
      setIsUpgradeModalOpen(true);
    };
    window.addEventListener("xavior:open-upgrade-modal", handler);
    return () => window.removeEventListener("xavior:open-upgrade-modal", handler);
  }, []);

  const shouldShowConnectionError = (err: Error | null) => {
    if (!err) return false;
    const message = err.message.toLowerCase();

    if (
      message.includes("401") ||
      message.includes("403") ||
      message.includes("unauthorized") ||
      message.includes("session invalid")
    ) {
      return false;
    }

    return (
      message.includes("failed to fetch") ||
      message.includes("network") ||
      message.includes("timeout") ||
      message.includes("timed out") ||
      message.includes("502") ||
      message.includes("503") ||
      message.includes("504") ||
      message.includes("server")
    );
  };

  // Toasts for the payment-confirm and OAuth-callback effects below — both run
  // at the App level so feedback shows regardless of which modal/view is open.
  const { showToast } = useToast();

  // Handle payment success redirect
  const paymentSessionIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (paymentStatusRef.current === null) {
      const params = new URLSearchParams(window.location.search);
      const paymentStatus = params.get("payment");
      paymentStatusRef.current = paymentStatus;
      // Capture session_id (Stripe replaces {CHECKOUT_SESSION_ID} on redirect)
      // before replaceState wipes the query string. /payment/confirm uses
      // this to look up the actual price from the Stripe subscription.
      paymentSessionIdRef.current = params.get("session_id");
      if (paymentStatus) {
        window.history.replaceState({}, "", window.location.pathname);
      }
    }

    const paymentStatus = paymentStatusRef.current;
    if (!paymentStatus) {
      return;
    }

    if (paymentStatus === "success") {
      if (!token) {
        return;
      }

      void (async () => {
        try {
          const response = await apiFetch<{
            success: boolean;
            accessToken?: string;
            user?: { isPaidAccount: boolean };
          }>("payment/confirm", {
            method: "POST",
            authToken: token,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(
              paymentSessionIdRef.current
                ? { sessionId: paymentSessionIdRef.current }
                : {},
            ),
          });
          if (response.success) {
            track("upgraded", { is_paid: response.user?.isPaidAccount === true });
          }
          // Update token with new JWT that has is_paid=true
          if (response.accessToken) {
            console.log(
              "[App] Payment confirmed - updating token with is_paid=true",
            );
            setToken(response.accessToken);
          }
        } catch (error) {
          console.error("Payment confirmation failed", error);
          // #5284: the checkout return is the one moment the user has just paid —
          // don't leave a failed confirm silent. Stripe's webhook reconciles the
          // subscription independently, so this is a delay, not a lost payment;
          // the error toast carries a Report path for support if it never lands.
          showToast(
            "Your payment went through, but we couldn't confirm it yet. Your plan should update in a few minutes — if it doesn't, reload the app or report the issue.",
            "error",
            {
              message: "Payment confirmation failed",
              context: `payment/confirm POST failed after checkout return: ${error instanceof Error ? error.message : String(error)}`,
            },
          );
        } finally {
          paymentStatusRef.current = null;
          await refresh();
          queryClient.invalidateQueries({ queryKey: ["workspace", "quota"] });
        }
      })();
    } else {
      paymentStatusRef.current = null;
      void refresh();
    }
  }, [refresh, token, setToken, showToast]);

  // Handle OAuth callback params (calendar_connected, integration_connected, error)
  // This runs at the App level so toasts show regardless of which modal/view is open
  // Feedback opener for the connect-first front door's declined-connect signal
  // (#4203). Wired here in the composition layer so ConnectPage stays a pure
  // features/auth screen with no app/providers or features/oauth import.
  const { openModal: openFeedbackModal, setDescription: setFeedbackDescription } =
    useFeedback();
  const oauthHandledRef = React.useRef(false);
  const clearFlagTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  React.useEffect(
    () => () => {
      if (clearFlagTimerRef.current) clearTimeout(clearFlagTimerRef.current);
    },
    [],
  );
  React.useEffect(() => {
    if (oauthHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);

    const calendarConnected = params.get("calendar_connected") === "true";
    const integrationConnected = params.get("integration_connected") === "true";
    const oauthError = params.get("error");

    // #5233: EVERY connect-return error code is handled — known codes keep their
    // specific copy, unknown codes (the callback forwards raw post-auth-hook
    // error messages) get a generic honest toast + the same URL cleanup instead
    // of the old silent early-return, and codes owned by other surfaces
    // (sso_* → LoginV2 on /login, fatsecret_* → the portal) map to null so their
    // params are left alone. Mapping lives with the connect flow in
    // features/oauth/lib/connect-provider.
    const oauthErrorMessage = oauthError
      ? describeConnectReturnError(oauthError, params.get("provider"))
      : null;
    const isOAuthError = oauthErrorMessage !== null;

    if (!calendarConnected && !integrationConnected && !isOAuthError) return;
    oauthHandledRef.current = true;

    // Only clean error params from URL — success params (calendar_connected,
    // integration_connected) are left for IntegrationsModal to consume so it
    // can call checkCalendarStatus() / loadIntegrations() before cleaning up.
    if (isOAuthError) {
      const url = new URL(window.location.href);
      url.searchParams.delete("error");
      url.searchParams.delete("provider");
      window.history.replaceState({}, "", url.toString());
    }

    // Extended 15s so later-mounting modules (Drive, Calendar behind lazy load)
    // can still pick up the flag. Tracked for cleanup on unmount.
    const scheduleClear = () =>
      setTimeout(
        () => sessionStorage.removeItem("email_just_connected"),
        15000,
      );
    if (calendarConnected || integrationConnected) {
      track("integration_connected", { kind: calendarConnected ? "calendar" : "integration" });
      sessionStorage.setItem("email_just_connected", "true");
      // Connect is now a full-page redirect, so THIS return handler is the single
      // post-connect dispatch point (the popup that used to emit these is gone):
      //  • integration-oauth-connected → mounted Gmail/Calendar modules re-initialize
      //  • xavior:integration-connected → MainLayout's centralized refetch
      //    (email/gmail/integration query groups + objects/modules/google-accounts)
      window.dispatchEvent(new CustomEvent("integration-oauth-connected"));
      window.dispatchEvent(new CustomEvent("xavior:integration-connected"));
      clearFlagTimerRef.current = scheduleClear();
    } else if (oauthErrorMessage) {
      showToast(oauthErrorMessage, "error");
    }
  }, [showToast]);

  // Post-auth landing (PDR 010). All auth paths redirect to the app root ("/")
  // after a verified identity, which flows through the main gate chain
  // (getMainContent → PlanGate → name → app), so PlanGate is the first-run front
  // door and is never bypassed. There is no /welcome route and no standalone
  // demo-or-connect landing page (LandingDecision, the #3756 login-flow rework,
  // was retired). In-app onboarding is the chat-window flow (useChatOnboarding
  // in ChatBox); the user-level 5-state machine + its OnboardingBeats transcript
  // were removed (#4420). See docs/product/pdr/010-first-run-login-and-onboarding-flow.md.

  // Determine what to show based on auth state
  const getMainContent = () => {
    // 1) Still checking auth (refreshing cookie or loading session) → show loading spinner
    //    CRITICAL: We must NOT redirect to /login while status === 'checking'
    if (status === "checking") {
      return <XaviorPulseLoader label="Restoring session…" />;
    }

    // 1.5) Suspended account (Road 6) → dedicated terminal screen. Must come
    //       BEFORE any unauthenticated/redirect branch: the token is still held
    //       (suspension is an account state, not an auth failure), so we never
    //       fall through to /login and never bounce the user around.
    if (status === "account_suspended") {
      return (
        <SuspendedAccountScreen email={profile?.email} onLogout={clearSession} />
      );
    }

    // 2) Unauthenticated with a connection error → show connection error screen
    if (
      status === "unauthenticated" &&
      error &&
      shouldShowConnectionError(error)
    ) {
      return (
        <ConnectionErrorScreen
          error={error}
          onRetry={() => {
            window.location.reload();
          }}
        />
      );
    }

    // 2) Definitively unauthenticated → redirect to login
    //    This can only happen AFTER the refresh cookie attempt has completed
    if (status === "unauthenticated") {
      return <Navigate to="/login" replace />;
    }

    // 3) We have a token but failed to load profile → show connection error screen
    if (!profile && error) {
      const handleClearAndRetry = () => {
        // Clear session storage to force refresh token flow
        if (typeof window !== "undefined") {
          sessionStorage.clear();
        }
        window.location.reload();
      };

      return (
        <ConnectionErrorScreen error={error} onRetry={handleClearAndRetry} />
      );
    }

    // 4) Token exists, no profile yet, and no error → treat as loading
    if (!profile) {
      return <XaviorPulseLoader />;
    }

    // 4.5) Conditional, post-identity Terms & Privacy consent (PDR 011 D8 / ADR
    //      031, #4204). Identity-level and a precondition to plan / connect /
    //      onboarding, so it gates BEFORE the paywall and every workspace gate —
    //      this is the seam the two-screen identity-first SSO model provides
    //      (identity → T&C → data grant). It applies to invitees too (D17). The
    //      signal is server-driven and INERT while `TC_VERSIONED_CONSENT` is OFF
    //      (`requiresConsent.required` is false → falls through to the legacy
    //      agreements gate / implicit footer #4224), so this never fires at
    //      launch. When the flag flips ON, this versioned flow owns consent.
    if (profile.requiresConsent?.required && token) {
      return (
        <VersionedTermsGate
          user={profile}
          authToken={token}
          requiresConsent={profile.requiresConsent}
          onAccepted={refresh}
        />
      );
    }

    // 5) Paywall / PlanGate — runs BEFORE the workspace setup screen so a
    //    fresh signup picks a plan and accepts agreements first. Returning
    //    users with `agreementsAcceptedAt` set + an active plan fall through
    //    immediately. See #2703.
    // Skip paywall on localhost unless explicitly enabled via VITE_ENABLE_PAYWALL
    const isLocalhost =
      typeof window !== "undefined" && window.location.hostname === "localhost";
    const forcePaywall = import.meta.env.VITE_ENABLE_PAYWALL === "true";
    const paywallEnabled =
      forcePaywall ||
      (!isLocalhost &&
        (import.meta.env.PROD || import.meta.env.MODE === "production"));

    // Avoid a setup-screen flash for fresh signups: while the workspace plan
    // tier is still loading, every paywall guard's `!tierLoading` clause
    // short-circuits to false even when the user has no agreements. The old
    // ordering then fell through to WorkspaceSetupScreen for a few frames
    // before PlanGate took over (#2703 follow-up). Holding on the pulse
    // loader until the tier resolves keeps the gate ordering deterministic
    // for users who actually need the paywall — i.e. those who haven't
    // accepted agreements yet. Returning users with `agreementsAcceptedAt`
    // set don't need to wait, since the paywall won't fire for them.
    if (
      paywallEnabled &&
      !profile.agreementsAcceptedAt &&
      workspacePlanTier === null
    ) {
      return <XaviorPulseLoader />;
    }

    const trialStatus = profile.trialStatus ?? null;

    // If trial status is missing entirely (supervisor unreachable), show connection error
    // instead of falling through to paywall. Only check when paywall is enabled (skip on localhost).
    if (
      paywallEnabled &&
      !profile.isPaidAccount &&
      !trialStatus &&
      !upgradeRequested &&
      profile.agreementsAcceptedAt
    ) {
      return (
        <ConnectionErrorScreen
          error={
            new Error(
              "Unable to verify account status. The server may be temporarily unavailable.",
            )
          }
          onRetry={() => window.location.reload()}
        />
      );
    }

    // Free Personal plan users are first-class after #1755/#1756. They only
    // hit PlanGate for the welcome terms gate (handled by PlanGate itself in
    // 'free_terms' mode) or for an explicit upgrade request.
    // Treat null (loading) as non-free to avoid flashing upgrade gates.
    const isFreePlan = workspacePlanTier === "free";
    const tierLoading = workspacePlanTier === null;

    // When versioned consent is live (TC_VERSIONED_CONSENT ON), the
    // VersionedTermsGate above is the SOLE consent authority — the backend
    // reports a non-`flag_off` reason only then. In that case the legacy
    // `!agreementsAcceptedAt` paywall clauses must NOT also fire (they'd
    // double-gate consent the versioned flow already handled). While the flag is
    // OFF (`flag_off` / signal absent) the legacy agreements gate governs
    // unchanged. NOTE: `trial` (PDR 011) is intentionally NOT `isFreePlan` — a
    // trial's paywall is governed by `trialStatus` (below) and, post-#4206/#4208,
    // the trial-lifecycle/upgrade flows; #4204 only owns the consent seam.
    const versionedConsentActive =
      profile.requiresConsent != null &&
      profile.requiresConsent.reason !== "flag_off";
    const legacyAgreementsPending =
      !versionedConsentActive && !profile.agreementsAcceptedAt;

    // Pending team invite always wins — render PlanGate so the team-join welcome
    // gate (#1776) can fire, regardless of plan tier or upgrade state.
    const hasPendingTeamInvite = (pendingInvites?.length ?? 0) > 0;

    // A non-owned container is a current team membership. PlanGate uses this
    // (plus pending invites) to identify a user with team history who must not
    // be force-ToS'd. Brand-new NON-invited free signups (no invites, no
    // memberships) keep force-ToS.
    const hasTeamMembership = (containers ?? []).some(
      (c) => c.isOwner === false,
    );

    // A usable workspace is any container the user owns or is a member of — i.e.
    // any entry in `containers` (a pending invite is NOT a container; membership
    // only exists after accepting). When the user has zero containers there is
    // nothing to land on, so closing a gate / having no pending invite must end
    // in logout rather than a stranded empty state (handled defensively below).
    const hasUsableWorkspace = (containers ?? []).length > 0;

    // Forced trial-expiry renders the fullscreen PlanGate (the gate condition
    // below, gated on the component-scope `expiredNeedsUpgrade` memo). Only the
    // VOLUNTARY upgrade path (?upgrade=1 / the xavior:open-upgrade-modal CTA)
    // opens PlanChoiceModal, which lives inside getMainContent()'s app-content
    // return so it can never stack over this or any gate (D1 reverted from
    // "modal-too" after the Phase 2 review confirmed the stacking/re-entry holes).
    const freePlanShouldShowPaywall =
      isFreePlan &&
      !tierLoading &&
      paywallEnabled &&
      legacyAgreementsPending;

    // Pending team invite — render the team-join welcome (#1776) whenever there
    // is a pending invite, UNLESS the user has dismissed THIS gate this session.
    // The dismissal is required so the gate's X actually works: closing calls
    // `handleTeamJoinDismiss` (onDismiss), and without this `!teamJoinDismissed`
    // guard the branch would re-render the gate on the next frame — making X a
    // no-op and (worse) trapping the invitee, since membership only exists after
    // accepting. With the guard, a closed gate falls through to the normal app
    // on an EXISTING workspace when the user has one (PlanGate selects it on
    // close), or to the defensive logout below when the user has none.
    //
    // CRITICAL: this uses a SEPARATE, in-memory dismissal (teamJoinDismissed),
    // NOT the monetization `paywallDismissed` sessionStorage flag. Sharing that
    // flag let a checkout-paywall dismissal (or a stale flag from another account
    // in the same tab) silently suppress a real invitee's team-join gate, even
    // trapping a zero-workspace invitee on an infinite loader. teamJoinDismissed
    // resets on identity (token) change, so an invitee ALWAYS sees the gate on a
    // fresh login regardless of any prior paywall dismissal. NEVER auto-join on
    // dismiss — the invite stays pending until explicitly accepted/ignored.
    if (hasPendingTeamInvite && !teamJoinDismissed) {
      return (
        <PlanGate
          user={profile}
          authToken={token}
          trialStatus={trialStatus}
          pendingInvites={pendingInvites}
          hasTeamMembership={hasTeamMembership}
          onDismiss={handleTeamJoinDismiss}
        />
      );
    }

    // Standalone zero-workspace plan-gate (Teams S4 / PDR 006 D4). A logged-in
    // user with NO usable workspace and no pending invite (e.g. a removed member,
    // or a fresh signup whose creation failed) is NEVER logged out — they land
    // here, still authenticated, and can create a Free workspace (or wait for an
    // invite). Ordered AFTER the pending-invite gate (so an open invite still
    // shows the join gate) and BEFORE the paywall branches so the free_terms
    // clause never evaluates for them even though useWorkspacePlanTier degrades a
    // null selectedContainer to 'free'.
    //
    // Only render the gate once the empty state is CONFIRMED (`containersLoaded`,
    // no error). While the containers fetch is still in flight (a slow cold
    // start), `hasUsableWorkspace` is also false but we must hold a neutral
    // loader instead of flashing the gate.
    if (!hasUsableWorkspace) {
      if (confirmedZeroWorkspace) {
        // PDR 011 §4 "NO workspace at all" leaf, gated on the per-user trial
        // clock (D11). Under the D18 connect-gate, a workspace exists only after
        // an email+calendar grant lands, so "no workspace" === "not connected"
        // for a personal identity. A brand-new, never-trialed, non-invitee
        // identity therefore lands on the connect-first front door (D6/D7/D18) —
        // no PlanGate at signup (D1). Invitees were already routed by the
        // pending-team-invite gate above and are excluded here via
        // hasTeamMembership. A LAPSED user (trial clock already set, workspace
        // hard-deleted at day 21) falls through to PlanGate so they get upgrade /
        // create-or-join-paid, never a second free trial (D15).
        if (
          resolveZeroWorkspaceDestination({
            trialStartedAt: profile.trialStartedAt,
            hasTeamMembership,
          }) === "connect"
        ) {
          // SSO identities already have a provider — skip the chooser and fire
          // the data-scope grant straight away (PDR 010 D3 auto-target). The
          // ConnectPage chooser is for magic-link (no provider). Guarded so a
          // denial that lands back here, or a failed start, falls back to the
          // chooser instead of looping.
          if (
            profile.loginProvider &&
            !ssoAutoConnectFailed &&
            !ssoAutoConnectAttemptedAtMount
          ) {
            const ssoProvider = profile.loginProvider;
            return (
              <SsoAutoConnect
                onAutoConnect={() =>
                  connectProvider({
                    provider: ssoProvider,
                    authToken: token,
                    redirectUrl: window.location.href,
                    loginHint: profile.email,
                  })
                }
                onError={handleSsoAutoConnectError}
              />
            );
          }
          return (
            <ConnectPage
              onConnect={(provider) =>
                connectProvider({
                  provider,
                  authToken: token,
                  redirectUrl: window.location.href,
                  loginHint: profile.email,
                })
              }
              onDeclinedFeedback={(tag) => {
                openFeedbackModal({ type: "feature_request" });
                setFeedbackDescription(`${tag} `);
              }}
            />
          );
        }
        return <PlanGate user={profile} authToken={token} zeroWorkspace />;
      }
      return <XaviorPulseLoader label="Loading your workspace…" />;
    }

    // Forced trial-expiry → fullscreen PlanGate (reverted from Phase 2 D1).
    // Voluntary upgrade (?upgrade=1 / CTA event) opens PlanChoiceModal inside
    // the authenticated app shell (see the final return below).
    if (!paywallDismissed && (expiredNeedsUpgrade || freePlanShouldShowPaywall)) {
      return (
        <PlanGate
          user={profile}
          authToken={token}
          trialStatus={trialStatus}
          pendingInvites={pendingInvites}
          hasTeamMembership={hasTeamMembership}
          onDismiss={handlePaywallDismiss}
        />
      );
    }

    // 6a) First-time workspace setup screen — only after the user has gotten
    //     past the paywall (above). Shown regardless of containerReady because
    //     free-tier users have containerReady=true and would never see it if
    //     it were gated on !containerReady.
    if (
      profile &&
      resolveWorkspaceOnboarding(selectedContainer, profile).needsOnboarding &&
      !setupScreenDismissed
    ) {
      return (
        <WorkspaceSetupScreen
          onReady={handleSetupReady}
          onTimeout={handleSetupTimeout}
          checkReady={checkWorkspaceReady}
        />
      );
    }

    // 6b) Container warmup gate for paid/trial users — actual provisioning is
    //     happening so the workspace-specific copy is accurate here. Returning
    //     users without a container (free tier) skip this gate entirely because
    //     `containerReady === true` for them (see SessionProvider). Issue #2410
    //     called out that this branch must not bleed into the generic
    //     "Loading your workspace…" splash that returning users saw on every
    //     refresh (#2516, #2568).
    //
    //     If warmup hasn't resolved within 20 s (#2475 stuck-state escape hatch),
    //     surface ConnectionErrorScreen so the user always has a retry action.
    if (!containerReady) {
      if (containerLoadingTimedOut) {
        return (
          <ConnectionErrorScreen
            error={
              new Error(
                "Workspace is taking longer than expected to start. The server may be temporarily unavailable.",
              )
            }
            onRetry={() => window.location.reload()}
          />
        );
      }
      return <XaviorPulseLoader label="Preparing your workspace…" />;
    }

    // Redirect legacy /onboarding route to home — onboarding is now conversational
    if (location.pathname === "/onboarding") {
      return <Navigate to="/" replace />;
    }

    // Name-your-workspace gate. Only an un-named TEAM is prompted now: an owner
    // whose team workspace hasn't been explicitly named (`nameSetByUser !== true`).
    // Solo workspaces are auto-named at creation (`{First}'s Workspace`) so they
    // never match. One-time per workspace (localStorage skip flag covers reloads;
    // a successful save flips `nameSetByUser` so the condition no longer matches).
    // Skippable. Gate logic lives in workspace-name-setup-gate.ts.
    if (
      shouldShowWorkspaceNameSetup({
        type: selectedContainer?.type,
        isOwner: selectedContainer?.isOwner,
        nameSetByUser: selectedContainer?.nameSetByUser,
        dismissed: workspaceNameDismissed,
        localStorageSkipFlag: hasWorkspaceNameSkipFlag(selectedContainer?.id),
      })
    ) {
      // Prefill so the field is never blank: the workspace's current name (an
      // upgraded solo space is already "{First}'s Workspace") unless that's the
      // generic default, in which case derive one from the owner's name. One tap
      // to accept, or type a real (e.g. firm) name.
      const currentName = selectedContainer?.containerName ?? "";
      const teamNamePrefill =
        currentName && currentName !== "Personal"
          ? currentName
          : suggestWorkspaceName(profile?.name ?? "") || currentName;
      return (
        <WorkspaceNameSetupScreen
          token={token}
          workspaceId={selectedContainer?.id}
          initialWorkspaceName={teamNamePrefill}
          isTeam={selectedContainer?.type === "team"}
          onDone={(skipped) => {
            if (skipped) setWorkspaceNameSkipFlag(selectedContainer?.id);
            setWorkspaceNameDismissed(true);
            if (!skipped) void refresh();
          }}
        />
      );
    }

    // 7) Chat-window onboarding (recipe replacement, D3). A brand-new user
    //    (`!profile.onboardingComplete`) past the paywall/setup/name gates now
    //    drops into the REAL app shell — same providers + AuthenticatedAppShell
    //    as the normal path — with `onboardingActive` true. The rail loads
    //    together with the chat at shell-readiness (like a returning user); the
    //    guided tour runs in the chat with the COMPOSER locked. The agent greets
    //    them as real assistant chat messages; when phase-1 sync is ready (or a
    //    timeout), useChatOnboarding ends the tour (unlocks the composer) and
    //    latches /onboarding/session/complete + refreshes the session so
    //    onboardingComplete flips true (releasing this gate with no reload). The
    //    legacy OnboardingRecipeHost/RecipeRunner/beats files are kept for now
    //    but are NO LONGER rendered by this gate (see Seam A).
    const onboardingActive =
      Boolean(profile) &&
      shouldUseRecipeOnboarding({ onboardingComplete: Boolean(profile?.onboardingComplete) });

    // Authenticated and paid (or dev mode) - show app with URL routing.
    // OnboardingActiveProvider owns the live onboarding-active state (seeded
    // from the gate); ChatBox's useChatOnboarding drives the tour and ends it
    // (unlocks the composer) when sync is ready.
    //
    // PlanChoiceModal is co-located here (inside the app-content return) so it
    // renders ONLY when the app shell renders — never stacked over a gate,
    // ConnectionErrorScreen, or setup screen. Re-open via CTA is always available.
    // Strip ?upgrade=1 on close (not on open) so upgradeRequested stays true
    // while the modal is open — this keeps the outage-suppression guard from
    // firing a dead-end ConnectionErrorScreen during a ?upgrade=1 + outage combo.
    const closeUpgrade = () => {
      setIsUpgradeModalOpen(false);
      if (upgradeRequested) {
        const p = new URLSearchParams(location.search);
        p.delete("upgrade");
        const s = p.toString();
        navigate(location.pathname + (s ? `?${s}` : "") + location.hash, {
          replace: true,
        });
      }
    };
    return (
      <>
        <AppProviders>
          <OnboardingActiveProvider initialActive={onboardingActive}>
            <ConversationProvider>
              <NativeInitializer />
              <FeedbackConversationBridge />
              <ConversationRouteSync>
                <AuthenticatedAppShell onboardingActive={onboardingActive} />
              </ConversationRouteSync>
            </ConversationProvider>
          </OnboardingActiveProvider>
        </AppProviders>
        <PlanChoiceModal
          context="upgrade"
          isOpen={isUpgradeModalOpen}
          authToken={token ?? undefined}
          onClose={closeUpgrade}
          onDismiss={closeUpgrade}
        />
      </>
    );
  };

  // Hold the entry loader across the post-onboarding reload (W6) — see
  // enteringApp above. Until the app is ready this prevents the gate chain from
  // mounting the shell mid-race (the blank); once ready (or after the timeout)
  // enteringApp clears and the normal routes render.
  if (enteringApp && !appEntryReady) {
    return <XaviorPulseLoader label="Setting up your workspace…" />;
  }

  return (
    <>
      {/* The nordic/atelier desk — rendered only while authenticated so it stays
          stable behind the post-auth loading gates without bleeding onto the
          (translucent) login screen. Self-gates to those themes; null otherwise. */}
      {token && <WoodCanvas />}
      {/* PDR 012: app-wide billing-issue modal — surfaces a 403 WORKSPACE_LOCKED
          write (lapsed workspace) regardless of which module triggered it. */}
      <BillingIssueModalHost />
      <Routes>
      {/* Portal routes — always accessible without user authentication */}
      <Route path="/portal/*" element={<PortalApp />} />

      {/* Nutrition connect link — client-facing, no user session required */}
      <Route path="/connect/nutrition" element={<NutritionConnect />} />

      {/* Magic link verification route - always accessible */}
      <Route path="/auth/verify" element={<MagicLinkVerify />} />

      {/* Login route */}
      <Route path="/login" element={<LoginV2 />} />

      {/* Legacy onboarding route — redirects to home */}
      <Route path="/onboarding" element={getMainContent()} />

      {/* Workspace settings — admin console for owners/admins */}
      <Route path="/settings" element={getMainContent()} />

      {/* Canonical workspace/conversation routes - require authentication */}
      <Route
        path="/w/:workspaceId/c/:conversationId/mod/:moduleId"
        element={getMainContent()}
      />
      <Route
        path="/w/:workspaceId/c/:conversationId/:view?"
        element={getMainContent()}
      />

      {/* DEV-only: mount the recipe onboarding directly, bypassing the entire
          first-run gate chain (paywall / setup / name / containerReady). For
          testing the recipe flow in isolation — uses the logged-in session token.
          Not a product route: gated on import.meta.env.DEV so prod builds never
          mount it (it fires real onboarding-latch POSTs on mount; #5277). */}
      {import.meta.env.DEV && (
        <Route path="/dev/onboarding" element={<OnboardingRecipeHost />} />
      )}

      {/* Root and catch-all - require authentication */}
      <Route path="/*" element={getMainContent()} />
    </Routes>
    </>
  );
}

export default function App() {
  // Runs before any providers or routing.
  if (isMaintenanceModeActive()) {
    return <MaintenanceOverlay />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          {/* DeepLinkHandler and OpenInAppBanner are outside SessionProvider
              so they work even when user is logged out */}
          <OpenInAppBanner />
          <SessionProvider>
            <DeepLinkHandler />
            <AnalyticsIdentify />
            {/* #5105: diagnostics host — sibling of AppContent so it survives the
                gate chain's early-return loaders. Opens on 5 rapid taps (loop-proof
                on the bare native loader) or the xavior:open-diag event. */}
            <DiagHost />
            <FeedbackProvider>
              <ErrorBoundary>
                <AppContent />
              </ErrorBoundary>
              <FeedbackModal />
              <SessionErrorBanner />
            </FeedbackProvider>
          </SessionProvider>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
